/**
 * StepWelcome — Step 0 of onboarding.
 * Introduces sharpelab's MCP-first model with a 3-step visual flow.
 */

import { BrandMark } from '../BrandLogo';

interface StepWelcomeProps {
  onNext: () => void;
}

interface FlowCardProps {
  number: string;
  title: string;
  description: string;
}

function FlowCard({ number, title, description }: FlowCardProps) {
  return (
    <div className="flex-1 min-w-0 rounded-xl border border-gray-700/60 bg-gray-800/40 p-4 space-y-2">
      <div className="w-8 h-8 rounded-full border-2 border-primary-600/60 bg-primary-900/30 flex items-center justify-center shrink-0">
        <span className="text-sm font-bold text-primary-400">{number}</span>
      </div>
      <p className="font-semibold text-sm text-gray-100">{title}</p>
      <p className="text-xs text-gray-400 leading-relaxed">{description}</p>
    </div>
  );
}

export function StepWelcome({ onNext }: StepWelcomeProps) {
  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <BrandMark className="w-10 h-10" />
        </div>
        <h1 className="font-display font-extrabold text-4xl text-white leading-tight">
          Welcome to sharpelab
        </h1>
        <p className="text-lg text-gray-300 leading-relaxed">
          Backtest, optimize and run crypto trading bots —
          driven by your favorite <span className="text-primary-300 font-medium">AI agent</span>.
        </p>
      </div>

      {/* Visual flow — three steps with chevrons between them */}
      <div className="flex flex-col sm:flex-row gap-3">
        <FlowCard
          number="1"
          title="Connect MCP"
          description="Add sharpelab to Codex, Claude Code, Cursor — or any MCP-capable agent."
        />
        <div className="hidden sm:flex items-center text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        <FlowCard
          number="2"
          title="Chat is the interface"
          description="From idea to deployed bot, your agent drives the whole flow."
        />
        <div className="hidden sm:flex items-center text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        <FlowCard
          number="3"
          title="Track every run"
          description="Every backtest, optimization, and bot session your agent runs shows up here."
        />
      </div>

      {/* CTA */}
      <div className="pt-2">
        <button
          type="button"
          onClick={onNext}
          className="btn-glow px-8 py-3 rounded-xl text-white font-semibold text-base"
        >
          Let's set it up
        </button>
      </div>
    </div>
  );
}
